/**
 * @fileoverview SVG visualization of a single room: walls, radiator,
 * preset valve + TRV, temperature and flow readouts, status badges.
 *
 * @module HydraulicBalancingTesting/components/RoomUnit
 */

import React from 'react';

import { SVG_DIM, COLORS, PRESET_POSITIONS, PIPES, RADIATOR } from '../constants';
import { toLitersPerHour, clamp } from '../physics';

import radiatorStart from '../radiator 3 start.png';
import radiatorMiddle from '../radiator 3 middle.png';
import radiatorEnd from '../radiator 3 end.png';
import thermostatIcon from '../thermostatRadiator.png';
import presetValveIcon from '../prefixedValve.png';

const { ROOM_WIDTH, ROOM_HEIGHT } = SVG_DIM;

/** On-screen radiator width range [px]; the right edge is fixed, width grows left. */
const RAD_MIN_W = 30;
const RAD_MAX_W = 86;
const RAD_SIZE_MIN = RADIATOR.SIZES[0];
const RAD_SIZE_MAX = RADIATOR.SIZES[RADIATOR.SIZES.length - 1];

/**
 * Maps a radiator's nominal power to its on-screen geometry. The total width is
 * LINEAR in nominal power between RAD_MIN_W and RAD_MAX_W, and the section count
 * (start/middle×n/end art) follows the width so the drawing stays proportionate.
 * Shared with PipeNetwork so the supply/return stubs line up with the artwork.
 *
 * @param {number} nominal - radiator nominal output at ΔT50 [W]
 * @returns {{ totalW:number, sections:number, sectionW:number }}
 */
export const radiatorLayout = (nominal) => {
  const t = clamp((nominal - RAD_SIZE_MIN) / (RAD_SIZE_MAX - RAD_SIZE_MIN), 0, 1);
  const totalW = RAD_MIN_W + t * (RAD_MAX_W - RAD_MIN_W);
  const sections = clamp(Math.round(totalW / 11), 3, 9);
  const sectionW = totalW / sections;
  return { totalW, sections, sectionW };
};

/** Temperature → display colour relative to setpoint. */
const tempColor = (T, Tset) => {
  const err = T - Tset;
  if (err < -1) return COLORS.COLD;
  if (err > 1) return COLORS.HOT;
  return COLORS.GOOD;
};

/** Deviation → display colour (diverging palette). */
export const deviationColor = (dev) => {
  if (dev <= -0.25) return COLORS.DEV_UNDER_STRONG;
  if (dev < -0.1) return COLORS.DEV_UNDER;
  if (dev <= 0.1) return COLORS.DEV_OK;
  if (dev < 0.25) return COLORS.DEV_OVER;
  return COLORS.DEV_OVER_STRONG;
};

/**
 * One room of the building.
 *
 * @param {Object} props
 * @param {Object} props.room - Room state (incl. live flow/temps)
 * @param {number} props.x - Room left edge [px]
 * @param {number} props.y - Room top edge [px]
 * @param {number} props.deviation - Flow deviation vs design (commissioning)
 * @param {boolean} props.isSelected
 * @param {boolean} props.showTargets - Scaffold: show deviation info
 * @param {boolean} props.dimmed - Mission scaffold: room not editable
 * @param {Function} props.onSelect - (roomIdx) => void
 * @param {boolean} props.radiatorEditable - radiator size can be cycled (sandbox)
 * @param {boolean} props.presetEditable - valve preset can be cycled
 * @param {Function} props.onCycleRadiator - (roomIdx) => void, cycle radiator size
 * @param {Function} props.onCyclePreset - (roomIdx) => void, cycle valve preset
 * @param {number} props.roomIdx - Flat index 0..8
 * @param {number} props.liveFlow - Current branch flow from the solver [m³/s]
 * @param {number} props.liveVelocity - Current branch velocity [m/s]
 */
export const RoomUnit = React.memo(({
  room,
  x,
  y,
  deviation,
  isSelected,
  showTargets,
  dimmed,
  onSelect,
  radiatorEditable,
  presetEditable,
  onCycleRadiator,
  onCyclePreset,
  roomIdx,
  liveFlow,
  liveVelocity,
}) => {
  const flowLph = toLitersPerHour(liveFlow);
  const noisy = liveVelocity > PIPES.NOISE_VELOCITY;
  const preset = PRESET_POSITIONS[room.presetIndex];

  // radiator art sizing: width is linear in nominal power, right edge fixed
  const { totalW, sections, sectionW } = radiatorLayout(room.radiatorNominal);
  const radH = 34;
  const radX = x + ROOM_WIDTH - 18 - totalW;
  const radY = y + ROOM_HEIGHT - radH - 12;

  return (
    <g
      onClick={() => onSelect(roomIdx)}
      style={{ cursor: 'pointer' }}
      opacity={dimmed ? 0.55 : 1}
    >
      {/* room body */}
      <rect
        x={x}
        y={y}
        width={ROOM_WIDTH}
        height={ROOM_HEIGHT}
        fill="white"
        stroke={isSelected ? COLORS.SECONDARY : COLORS.WALL}
        strokeWidth={isSelected ? 4 : 2}
      />

      {/* header: name + id badge */}
      <text x={x + 8} y={y + 16} fontSize={11} fill={COLORS.GRAY}>
        {room.name}
      </text>
      <circle
        cx={x + ROOM_WIDTH - 16}
        cy={y + 14}
        r={10}
        fill={isSelected ? COLORS.SECONDARY : COLORS.PRIMARY}
      />
      <text
        x={x + ROOM_WIDTH - 16}
        y={y + 18}
        fontSize={11}
        fill="white"
        textAnchor="middle"
        fontWeight="bold"
      >
        {room.id}
      </text>

      {/* temperature */}
      <text x={x + 10} y={y + 48} fontSize={22} fontWeight={600} fill={tempColor(room.T, room.Tset)}>
        {room.T.toFixed(1)}°C
      </text>
      <text x={x + 10} y={y + 64} fontSize={10} fill={COLORS.GRAY}>
        set {room.Tset.toFixed(1)}°C
      </text>

      {/* TRV opening indicator (small vertical gauge) */}
      <g>
        <rect x={x + ROOM_WIDTH - 18} y={y + 34} width={6} height={32} fill="#eee" stroke={COLORS.BORDER} strokeWidth={0.5} rx={2} />
        <rect
          x={x + ROOM_WIDTH - 18}
          y={y + 34 + 32 * (1 - room.trv)}
          width={6}
          height={32 * room.trv}
          fill={COLORS.SECONDARY}
          rx={2}
        />
        <text x={x + ROOM_WIDTH - 26} y={y + 44} fontSize={8} fill={COLORS.GRAY} textAnchor="end">
          TRV
        </text>
        <text x={x + ROOM_WIDTH - 26} y={y + 54} fontSize={8} fill={COLORS.GRAY} textAnchor="end">
          {(room.trv * 100).toFixed(0)}%
        </text>
      </g>

      {/* radiator artwork — click to cycle radiator size */}
      <g
        onClick={radiatorEditable ? (e) => { e.stopPropagation(); onCycleRadiator(roomIdx); } : undefined}
        style={radiatorEditable ? { cursor: 'pointer' } : undefined}
      >
        {radiatorEditable && (
          <rect
            x={radX - 2}
            y={radY - 14}
            width={totalW + 4}
            height={radH + 16}
            fill="transparent"
          />
        )}
        <image href={radiatorStart} x={radX} y={radY} width={sectionW} height={radH} preserveAspectRatio="none" />
        {Array.from({ length: sections - 2 }).map((_, i) => (
          <image
            key={i}
            href={radiatorMiddle}
            x={radX + sectionW * (i + 1)}
            y={radY}
            width={sectionW}
            height={radH}
            preserveAspectRatio="none"
          />
        ))}
        <image href={radiatorEnd} x={radX + sectionW * (sections - 1)} y={radY} width={sectionW} height={radH} preserveAspectRatio="none" />
        <text x={radX + (sections * sectionW) / 2} y={radY - 4} fontSize={9} fill={COLORS.GRAY} textAnchor="middle">
          {room.radiatorNominal} W
        </text>
        {radiatorEditable && <title>Click to change radiator size</title>}
      </g>

      {/* preset valve + TRV head icons — click the valve to cycle the preset */}
      <g
        onClick={presetEditable ? (e) => { e.stopPropagation(); onCyclePreset(roomIdx); } : undefined}
        style={presetEditable ? { cursor: 'pointer' } : undefined}
      >
        {presetEditable && (
          <rect x={x + 6} y={y + ROOM_HEIGHT - 62} width={56} height={42} fill="transparent" />
        )}
        <image href={presetValveIcon} x={x + 10} y={y + ROOM_HEIGHT - 46} width={20} height={20} />
        <text x={x + 10} y={y + ROOM_HEIGHT - 50} fontSize={10} fontWeight="bold" fill={COLORS.PRIMARY}>
          preset {preset.label}
        </text>
        {presetEditable && <title>Click to change valve preset</title>}
      </g>
      <image href={thermostatIcon} x={x + 32} y={y + ROOM_HEIGHT - 48} width={22} height={22} />

      {/* flow readout */}
      <text x={x + 10} y={y + ROOM_HEIGHT - 10} fontSize={11} fontWeight={600} fill={COLORS.RETURN}>
        {flowLph.toFixed(0)} l/h
      </text>
      {showTargets && (
        <text x={x + 70} y={y + ROOM_HEIGHT - 10} fontSize={11} fontWeight={600} fill={deviationColor(deviation)}>
          {deviation >= 0 ? '+' : ''}{(deviation * 100).toFixed(0)}%
        </text>
      )}

      {/* noise warning */}
      {noisy && (
        <g>
          <text x={x + ROOM_WIDTH - 38} y={y + ROOM_HEIGHT - 10} fontSize={12}>
            🔊
            <title>Flow velocity {liveVelocity.toFixed(2)} m/s — valve noise likely</title>
          </text>
        </g>
      )}
    </g>
  );
});

RoomUnit.displayName = 'RoomUnit';
